/**
 * @file stock-level-format.ts
 * @description Формат строки остатка и контракт её отрисовки
 * (phase19.24 этап 1, шаг 4; плоская таблица партий — phase19.31, SC59).
 *
 * Таблица (`≥lg`) и компактный список (`<lg`) показывают одни и те же данные,
 * поэтому формат живёт здесь: иначе «без места» на телефоне и на десктопе
 * начинают писаться разными словами, а срок годности — разным форматом даты.
 *
 * Отдельно от `StockLevelCells.tsx`: там компоненты, здесь чистые функции и
 * типы — файл с обоими сортами экспортов ломает fast-refresh (правило
 * `react-refresh/only-export-components`).
 */

import type { ReactNode } from "react";

import { shortDocNumber } from "../short-number";
import { formatQtyValue } from "../../../lib/picking-format";
import type { StockLevelDto } from "../../../lib/api-wms";

/** Общий контракт таблицы и компактного списка остатков. */
export interface StockRowViewProps {
  items: StockLevelDto[];
  /** Галочки видны только в отборе «Без места» или по этикеткам. */
  selectable: boolean;
  isSelected: (level: StockLevelDto) => boolean;
  onToggle: (level: StockLevelDto) => void;
  /** Меню «⋯» строки — собирает вызывающий (`stockRowActions`). */
  actions: (level: StockLevelDto) => ReactNode;
  /**
   * phase19.22 — можно ли отметить строку и подпись, если нельзя. Не передано —
   * правило размещения («Без места»).
   */
  selectState?: (level: StockLevelDto) => { can: boolean; hint: string };
  /** phase19.22 (W17-9c) — отбор по этикеткам: колонка «Этикетки» и количество у отмеченных. */
  labelsMode?: boolean;
  /** Количество этикеток строки (по умолчанию — остаток партии). */
  qtyOf?: (level: StockLevelDto) => number;
  onQty?: (level: StockLevelDto, qty: number) => void;
  /** SC59: колонка «Склад» — только когда складов несколько и склад не выбран. */
  showWarehouse: boolean;
}

/** Количество без единицы: единицу счёта фасовки остаток не отдаёт. */
export function formatStockQty(value: string | number): string {
  return Number(formatQtyValue(String(value))).toLocaleString("ru-RU");
}

/** «Доступно» = количество − резерв (SC59). Мусор в числе считаем нулём. */
export function stockAvailable(level: Pick<StockLevelDto, "quantity" | "totalReserved">): number {
  const quantity = Number(level.quantity);
  const reserved = Number(level.totalReserved);
  const safe = (n: number) => (Number.isFinite(n) ? n : 0);
  // Округление до тысячных: дробные фасовки (кг) не должны давать 0,30000000004.
  return Math.round((safe(quantity) - safe(reserved)) * 1000) / 1000;
}

/** Номер партии коротко («RCP-…018-1») либо «без партии». */
export function stockLotShort(level: StockLevelDto): string {
  return level.lot ? shortDocNumber(level.lot.batchNumber) : "без партии";
}

/**
 * Срок годности — поле «только дата»: сервер хранит полночь UTC
 * («2027-05-17T00:00:00.000Z»). Читаем его в UTC, иначе западнее Гринвича
 * дата съезжала на день раньше (ревью phase19.31).
 */
const EXPIRY_DATE: Intl.DateTimeFormatOptions = { timeZone: "UTC" };
const EXPIRY_DATE_SHORT: Intl.DateTimeFormatOptions = { timeZone: "UTC", day: "2-digit", month: "2-digit", year: "2-digit" };

/** «L-240517 · до 17.05.2027» либо «без партии» — подпись для печати и окон. */
export function stockLotText(level: StockLevelDto): string {
  if (!level.lot) return "без партии";
  const expires = level.lot.expiresAt
    ? ` · до ${new Date(level.lot.expiresAt).toLocaleDateString("ru-RU", EXPIRY_DATE)}`
    : "";
  return `${level.lot.batchNumber}${expires}`;
}

/** Тон ячейки «Годен до»: обычный, «внимание» (срок обязателен, но пуст), пусто. */
export type StockExpiryTone = "default" | "warning" | "muted";

export interface StockExpiryView {
  /** Таблица: «21.09.2027», «не указан» или «—». */
  text: string;
  /** Компактный список: «до 21.09.27», «срок не указан»; `null` — не показывать. */
  short: string | null;
  tone: StockExpiryTone;
}

/**
 * Колонка «Годен до» (SC59): дата; «не указан» янтарным, когда срок у
 * фасовки обязателен, а партия заведена без него; «—», когда срок не ведётся
 * или партии нет.
 */
export function stockExpiryView(level: StockLevelDto): StockExpiryView {
  const lot = level.lot;
  if (lot?.expiresAt) {
    const date = new Date(lot.expiresAt);
    return {
      text: date.toLocaleDateString("ru-RU", EXPIRY_DATE),
      short: `до ${date.toLocaleDateString("ru-RU", EXPIRY_DATE_SHORT)}`,
      tone: "default",
    };
  }
  if (lot?.expiryRequired) return { text: "не указан", short: "срок не указан", tone: "warning" };
  return { text: "—", short: null, tone: "muted" };
}

/**
 * Показывать ли колонку «Склад» (SC59): нет, если склад выбран в фильтре или
 * у магазина он один. Справочник складов мог не загрузиться — тогда судим по
 * самим строкам: все из одного склада — колонка лишняя.
 */
export function shouldShowWarehouse(
  warehouseCount: number,
  filterWarehouseId: string,
  rows: Pick<StockLevelDto, "warehouseId">[],
): boolean {
  if (filterWarehouseId) return false;
  if (warehouseCount > 0) return warehouseCount > 1;
  return new Set(rows.map((row) => row.warehouseId)).size > 1;
}

/**
 * Поиск по строке остатка: название фасовки, артикул, номер партии и её код
 * для скана (SC59: отдельного поля «Партия» нет — всё в одном поиске).
 */
export function matchesStockSearch(level: StockLevelDto, name: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [name, level.sku?.code, level.lot?.batchNumber, level.lot?.code].some((part) =>
    (part ?? "").toLowerCase().includes(needle),
  );
}
